// For info on making requests - https://postgrest.org/en/v5.0/api.html

import dates from 'src/utils/dates'
import querystring from 'src/utils/querystring'

import http from './http'

const api = {
  demo: false,

  get: null,
  post: null,
  put: null,

  // @TODO Assert request is allowed in demo mode, else reject request.
  async stub(method) {
    const { default: stub } = await import('./stub')

    return (uri, config) => {
      const useStub = stub.request(uri, config)
      return useStub || http[method](uri, config)
    }
  },
  async set(demo) {
    api.demo = demo

    if (demo) {
      const promises = [api.stub('get'), api.stub('post'), api.stub('put')]
      const [get, post, put] = await Promise.all(promises)

      api.get = get
      api.post = post
      api.put = put
    } else {
      api.get = http.get.bind(http)
      api.post = http.post.bind(http)
      api.put = http.put.bind(http)
    }
  },

  getLimitAndOffset({ limit, offset }) {
    const setLimit = typeof limit === 'number' ? `&limit=${limit}` : ''
    const setOffset = typeof offset === 'number' ? `&offset=${offset}` : ''
    return `${setLimit}${setOffset}`
  },
  getParameters(params) {
    const validProps = {
      clicks: { apiKey: 'clicks' },
      ctr: { apiKey: 'ctr' },
      delivered: { apiKey: 'impressionsYesterday' },
      endDate: { apiKey: 'endDate' },
      isMissingCreatives: { apiKey: 'isMissingCreatives' },
      osi: { apiKey: 'osi' },
      priority: { apiKey: 'type' },
      startDate: { apiKey: 'startDate' },
      status: { apiKey: 'status' },
    }
    const apiOperators = {
      eq: 'eq',
      excl: 'neq',
      gte: 'gte',
      incl: 'eq',
      is: 'is',
      lte: 'lte',
      neq: 'neq',
      not: 'not',
      range: 'range',
      relative: 'range',
    }
    const propNames = Object.keys(validProps)
    const results = propNames.reduce((paramArray, currentPropName) => {
      const { apiKey } = validProps[currentPropName]
      const currentProp = params[currentPropName]

      if (!currentProp) return paramArray
      if (Array.isArray(currentProp) && !currentProp.length) return paramArray

      const { and, operator, value } = currentProp
      const getAllValues = (property) => property.map((prop) => {
        const { operator: optionOperator, value: optionValue } = prop

        return `${apiKey}=${apiOperators[optionOperator]}.${optionValue}`
      })
      const getValueByType = (val) => {
        const getAll = Array.isArray(val)

        return getAll
          ? getAllValues(val).join('&')
          : `${apiKey}=${apiOperators[operator]}.${val}`
      }
      const queryStringParam = Array.isArray(currentProp)
        ? getAllValues(currentProp).join('&')
        : getValueByType(value)

      if (and) {
        paramArray.push(`and=(${queryStringParam})`)
      } else {
        paramArray.push(queryStringParam)
      }

      return paramArray
    }, [])

    return results.length ? results.join('&') : ''
  },

  getDataRefresh() {
    return this.get('reports/data_refresh')
  },

  /**
   * Gets data for the AdX Health Report.
   * @param {string[]} dateRange
   * @param {number|string} publisherId
   * @return {Promise<ApiResponse>}
   */
  getHealthAdx({ dateRange, publisherId }) {
    const endpoint = '/reports/programmatic_health_adx'
    const dateQuery = querystring.dates(dateRange)

    return this.get(`${endpoint}?publisherId=eq.${publisherId}&${dateQuery}`)
  },
  /**
   * Gets data for the Network Health Report.
   * @param {Array} dateRange
   * @param {number|string} publisherId
   * @return {Promise<ApiResponse>}
   */
  getHealthNetwork({ dateRange, publisherId }) {
    const endpoint = '/reports/programmatic_health_network'
    const dateQuery = querystring.dates(dateRange)

    return this.get(`${endpoint}?publisherId=eq.${publisherId}&${dateQuery}`)
  },
  /**
   * Gets data for the AdX/OB report, fka the Yield Partner Report.
   * @param {Array} dateRange
   * @param {number|string} publisherId
   * @return {Promise<ApiResponse>}
   */
  getYieldPartner({ dateRange, publisherId }) {
    const endpoint = '/reports/programmatic_yield_partner'
    const dateQuery = querystring.dates(dateRange)

    return this.get(`${endpoint}?publisherId=eq.${publisherId}&${dateQuery}`)
  },
  getPublishers(publisherId) {
    const isPublisherIdArray = Array.isArray(publisherId)
    const validPublisherId = publisherId && !isPublisherIdArray
    const validPublisherIdArray = isPublisherIdArray && publisherId.length
    const passedPublisherIds = validPublisherId || validPublisherIdArray

    if (passedPublisherIds) {
      if (isPublisherIdArray) {
        return this.get(`/master_data/publishers?publisherId=in.(${publisherId})`)
      }

      return this.get(`/master_data/publishers?publisherId=eq.${publisherId}`)
    }

    return this.get('/master_data/publishers?isActive=is.true')
  },
  getPublishersByBillingCodes(billingCode) {
    const billingCodes = Array.isArray(billingCode)
      ? billingCode.join(',')
      : billingCode

    return this.get(`/master_data/publishers?billingCode=in.(${billingCodes})`)
  },

  getPersonarchy() {
    return this.post('/timesheet/rpc/get_personarchy')
  },
  getPersonByEmail(personEmail) {
    return this.get(`/timesheet/persons?personEmail=eq.${personEmail}`)
  },
  getPersonById(personId) {
    return this.get(`/timesheet/persons?personId=eq.${personId}`)
  },
  getPersons() {
    return this.get('/timesheet/persons?order=personNameFirst')
  },

  getInternalTeams() {
    return this.get('/timesheet/teams')
  },
  getInternalTeamById(teamId) {
    return this.get(`/timesheet/teams?teamId=eq.${teamId}`)
  },

  getInternalRoles() {
    return this.get('/timesheet/roles')
  },
  getInternalRoleById(teamId) {
    return this.get(`/timesheet/roles?roleId=eq.${teamId}`)
  },

  getTeamsByPublisherId(publisherId, teamIds) {
    const teamId = Array.isArray(teamIds) ? teamIds.join(',') : teamIds
    const teamString = teamId && teamId !== '-1' ? `&teamId=in.(${teamId})` : ''
    const query = `?publisherId=eq.${publisherId}${teamString}`

    return this.get(`/master_data/teams${query}`)
  },
  getTeams(publisherId, teamIds) {
    if (publisherId) {
      return this.getTeamsByPublisherId(publisherId, teamIds)
    }

    return this.get('/master_data/teams')
  },
  getClients(billingCode) {
    if (billingCode) {
      return this.get(`/timesheet/clients?billingCode=eq.${billingCode}`)
    }

    return this.get('/timesheet/clients?order=clientName')
  },

  getProjectById(projectId) {
    return this.get(`/timesheet/projects?projectId=eq.${projectId}`)
  },
  getProjectByCode(projectCode) {
    return this.get(`/timesheet/projects?projectCode=eq.${projectCode}`)
  },
  getProjects() {
    return this.get('/timesheet/projects?order=projectName')
  },

  getServices() {
    return this.get('/timesheet/services?order=serviceName')
  },
  getTimeTypes() {
    return this.get('/timesheet/time_classification_types')
  },

  getTimeEntriesByBillingCode({ billingCode }) {
    return this.get(`/timesheet/view_time_entries?billingCode=eq.${billingCode}`)
  },
  getTimeEntriesByDateRange({ endDate, startDate }) {
    return this.get(`/timesheet/view_time_entries?timeEntryDate=gte.${startDate}&timeEntryDate=lte.${endDate}`)
  },
  getTimeEntriesByDateRangeForUser({ endDate, personId, startDate }) {
    return this.get(`/timesheet/view_time_entries?personId=eq.${personId}&timeEntryDate=gte.${startDate}&timeEntryDate=lte.${endDate}`)
  },

  getClientAssignments({ billingCode }) {
    return this.get(`/timesheet/view_client_assignments?billingCode=eq.${billingCode}`)
  },

  modifyOrganization(body) {
    // { action_type: String(add|delete|update) }
    // Required for UPDATE & DELETE: { client_id }
    // CREATE requires e/t except clientId: {client_name, billing_code, is_administrative}
    // UPDATE: can include any as long as include clientId
    return this.post('/timesheet/rpc/modify_client', body)
  },
  modifyPerson(body) {
    // { action_type: String(add|delete|update) }
    // Required for UPDATE & DELETE: { person_id }
    // CREATE requires e/t except person_id
    // UPDATE: can include any as long as include person_id
    return this.post('/timesheet/rpc/modify_person', body)
  },
  modifyProject(body) {
    return this.post('/timesheet/rpc/modify_project', body)
  },

  getWeek(time_shift = 0) {
    return this.post('/timesheet/rpc/get_week', { time_shift })
  },
  createTimeEntry(data) {
    // Required Body: { time_entry_date: Date, person_id: Integer, client_id: Integer, time_classification_id: Integer, duration: Integer, person_email: String }
    // Optional Body Parameters: { service_id: Integer, description: String, project_id: Integer }
    return this.post('/timesheet/rpc/add_entry', data)
  },
  updateTimeEntry(data) {
    // Required Body: { time_entry_id: Integer, person_email: String }
    // Optional Body Parameters: { time_entry_date: Date, client_id: Integer, time_classification_id: Integer, duration: Integer, service_id: Integer, description: String, project_id: Integer }
    return this.post('/timesheet/rpc/update_entry', data)
  },
  deleteTimeEntry(data) {
    // Required Body: { time_entry_id: Integer, person_email: String }
    return this.post('/timesheet/rpc/delete_entry', data)
  },
  getEntries(data) {
    // Required Body: { person_email: String, include_delegates: Boolean }
    return this.post('/timesheet/rpc/get_entries', data)
  },

  getLineItemsForPublisher({ limit, offset = 0, publisherId, teamId }) {
    const teamString = teamId && ['-1', 'all-secondary'].indexOf(teamId) === -1 ? `&teamIds=cs.{${teamId}}` : ''
    const limitAndOffset = api.getLimitAndOffset({ limit, offset })

    return this.get(
      `/reports/pacing_line_items_summary?publisherId=eq.${publisherId}${teamString}${limitAndOffset}`,
    )
  },
  getOrderById({ orderId, publisherId }) {
    const queryString = orderId && ['-1', 'all-secondary'].indexOf(orderId) === -1 ? `&orderIds=cs.{${orderId}}` : ''
    return this.get(
      `/trafficking_objects/orders?publisherId=eq.${publisherId}${queryString}`,
    )
  },
  getOrdersForPublisher({ limit, offset, publisherId, teamId }) {
    const teamString = teamId && ['-1', 'all-secondary'].indexOf(teamId) === -1 ? `&teamIds=cs.{${teamId}}` : ''
    const limitAndOffset = api.getLimitAndOffset({ limit, offset })
    return this.get(
      `/trafficking_objects/orders?publisherId=eq.${publisherId}${teamString}${limitAndOffset}`,
    )
  },
  getLineItemById({ lineItemId, publisherId, teamId }) {
    const teamString = teamId && ['-1', 'all-secondary'].indexOf(teamId) === -1 ? `&teamIds=cs.[${teamId}` : ''

    return this.get(
      `/reports/pacing_line_items_summary?lineItemId=eq.${lineItemId}&publisherId=eq.${publisherId}${teamString})`,
    )
  },

  getGamOrders(query) {
    const { networkCode } = query

    return this.get(`/gam/orders/${networkCode}`)
  },
  getGamLineItems(query) {
    const { networkCode, orderId } = query

    return this.post(`/gam/line-items/${networkCode}`, { ids: { orderId } })
  },
  getGamLicas(query) {
    const { licaIds, networkCode } = query

    return this.post(`/gam/licas/${networkCode}`, { licaIds })
  },

  postGamCreatives(query) {
    const { ids, networkCode } = query

    return this.post(`/gam/creatives/${networkCode}`, { ids })
  },
  postTearsheet(query) {
    const { creativeId, lineItemId, networkCode, siteUrl } = query

    return this.post(`/gam/licas/${networkCode}/${lineItemId}/${creativeId}`, { siteUrl })
  },
  getPublisherDiscrepancy({ adjuster, publisherId, teamId }) {
    const endpoint = (adjuster && 'discrepancy_line_items') || 'discrepancy_line_items_beta'
    const teamString = teamId && ['-1', 'all-secondary'].indexOf(teamId) === -1 ? `&teamIds=cs.{${teamId}}` : ''
    return this.get(
      `/reports/${endpoint}?publisherId=eq.${publisherId}${teamString}`,
    )
  },
  getDiscrepancyCreatives({ adjuster, lineItemCode, publisherId, teamId }) {
    const endpoint = (adjuster && 'discrepancy_creatives') || 'discrepancy_creatives_beta'
    const teamString = teamId && teamId !== 'all-secondary' ? `&teamIds=cs.{${teamId}}` : ''

    return this.get(
      `/reports/${endpoint}?lineItemCode=eq.${lineItemCode}&publisherId=eq.${publisherId}${teamString}`,
    )
  },
  getCreativesReport({ lineItemId, publisherId, teamIds }) {
    const teamString = teamIds && teamIds.length ? `&teamIds=cs.{${teamIds.join(',')}}` : ''

    const endDateValue = dates.now()
    const startDateValue = endDateValue.clone().subtract(10, 'day')
    const startDateFormattedString = dates.getFormattedDate(startDateValue)
    const endDateFormattedString = dates.getFormattedDate(endDateValue)

    const queryString = `publisherId=eq.${publisherId}${teamString}&lineItemId=eq.${lineItemId}&and=(date.gte.${startDateFormattedString},date.lte.${endDateFormattedString})`

    return this.get(`/reports/delivery_creatives?${queryString}`)
  },
  getDeliveryReportDataForLineItemWithDates({
    lineItemId,
    publisherId,
    startDate,
    teamIds,
    endDate,
  }) {
    const teamString = teamIds && teamIds.length ? `&teamIds=cs.{${teamIds}}` : ''
    const startDateFormattedString = dates.getFormattedDate(startDate)
    const endDateFormattedString = dates.getFormattedDate(endDate)

    return this.get(
      `/reports/delivery_line_items?lineItemId=eq.${lineItemId}&and=(date.gte.${startDateFormattedString},date.lte.${endDateFormattedString})&publisherId=eq.${publisherId}${teamString}`,
    )
  },
  getPacingReportDataForLineItemWithDates({
    lineItemId,
    publisherId,
    startDate,
    teamIds,
    endDate,
  }) {
    const teamString = teamIds && teamIds.length ? `&teamIds=cs.{${teamIds}}` : ''
    const startDateFormattedString = dates.getFormattedDate(startDate)
    const endDateFormattedString = dates.getFormattedDate(endDate)

    return this.get(
      `/reports/pacing_line_items?lineItemId=eq.${lineItemId}&and=(date.gte.${startDateFormattedString},date.lte.${endDateFormattedString})&publisherId=eq.${publisherId}${teamString}`,
    )
  },

  AT_RISK(request) {
    const { isAlert = true, params, publisherId, teamId } = request
    const limitString = isAlert ? 'limit=1&' : ''
    const teamString = teamId && teamId !== 'all-secondary' ? `&teamIds=cs.{${teamId}}` : ''
    const queryString = api.getParameters(params)

    return this.get(
      `/reports/pacing_line_items_summary?${limitString}publisherId=eq.${publisherId}${teamString}&${queryString}`,
    )
  },

  STUCK_IN_PAUSED(request) {
    const { isAlert = true, params, publisherId, teamId } = request
    const queryString = api.getParameters(params)
    const limitString = isAlert ? 'limit=1&' : ''
    const teamString = teamId && teamId !== 'all-secondary' ? `&teamIds=cs.{${teamId}}` : ''

    return this.get(
      `/reports/pacing_line_items_summary?${limitString}publisherId=eq.${publisherId}${teamString}&${queryString}`,
    )
  },

  STUCK_IN_READY(request) {
    const { isAlert = true, params, publisherId, teamId } = request
    const queryString = api.getParameters(params)
    const limitString = isAlert ? 'limit=1&' : ''
    const teamString = teamId && teamId !== 'all-secondary' ? `&teamIds=cs.{${teamId}}` : ''

    return this.get(
      `/reports/pacing_line_items_summary?${limitString}publisherId=eq.${publisherId}${teamString}&${queryString}`,
    )
  },

  MISSING_CREATIVES(request) {
    const { isAlert = true, params, publisherId, teamId } = request
    const queryString = api.getParameters(params)
    const limitString = isAlert ? 'limit=1&' : ''
    const teamString = teamId && teamId !== 'all-secondary' ? `&teamIds=cs.{${teamId}}` : ''

    return this.get(
      `/reports/pacing_line_items_summary?${limitString}publisherId=eq.${publisherId}${teamString}&${queryString}`,
    )
  },

  ZERO_IMPRESSIONS(request) {
    const { isAlert = true, params, publisherId, teamId } = request
    const queryString = api.getParameters(params)
    const limitString = isAlert ? 'limit=1&' : ''
    const teamString = teamId && teamId !== 'all-secondary' ? `&teamIds=cs.{${teamId}}` : ''

    return this.get(
      `/reports/pacing_line_items_summary?${limitString}publisherId=eq.${publisherId}${teamString}&${queryString}`,
    )
  },

  ENDING_SOON(request) {
    const { isAlert = true, params, publisherId, teamId } = request
    const queryString = api.getParameters(params)
    const limitString = isAlert ? 'limit=1&' : ''
    const teamString = teamId && teamId !== 'all-secondary' ? `&teamIds=cs.{${teamId}}` : ''

    return this.get(
      `/reports/pacing_line_items_summary?${limitString}publisherId=eq.${publisherId}${teamString}&${queryString}`,
    )
  },
}

export default api
